import { DEFAULT_SALES_TAX_RATE, roundMoney } from './orderSalesTax';
import { PAYMENT_ADJUSTMENT_TYPES } from '../firebase/schema';

export { PAYMENT_ADJUSTMENT_TYPES };

export function emptyPaymentBreakdown() {
  return {
    taxableSubtotal: 0,
    nontaxableSubtotal: 0,
    taxAmount: 0,
    taxRate: DEFAULT_SALES_TAX_RATE,
    adjustments: []
  };
}

export function discountLabelFromOrder(order) {
  const discount = order?.discount;
  if (!discount || discount.amount == null || discount.amount === '') return 'Discount';
  if (discount.type === 'percent') return `Discount (${discount.amount}%)`;
  return 'Discount';
}

/**
 * Portal payment.breakdown + amountDue from AL invoice math.
 *
 * taxableSubtotal is the post-discount taxable base. Discount is listed as
 * taxable_discount for display only and does not enter the gross-total invariant.
 * Untaxed fees land in nontaxableSubtotal (also post-discount share).
 */
export function buildPaymentBreakdown({
  subtotal,
  feesTotal,
  taxableFeesTotal,
  discountAmount,
  isTaxExempt,
  taxRate = DEFAULT_SALES_TAX_RATE,
  discountLabel = 'Discount'
}) {
  const roundedSubtotal = roundMoney(subtotal);
  const roundedFeesTotal = roundMoney(feesTotal);
  const roundedTaxableFees = roundMoney(taxableFeesTotal);
  const roundedNontaxableFees = roundMoney(Math.max(0, roundedFeesTotal - roundedTaxableFees));
  const roundedDiscount = roundMoney(discountAmount);

  const preTaxFull = roundedSubtotal + roundedFeesTotal;
  const preTaxTaxable = roundedSubtotal + roundedTaxableFees;

  let discountOnTaxable = 0;
  if (roundedDiscount > 0 && preTaxFull > 0) {
    discountOnTaxable = roundMoney(roundedDiscount * (preTaxTaxable / preTaxFull));
  }
  const discountOnNontaxable = roundMoney(roundedDiscount - discountOnTaxable);

  const taxableSubtotal = roundMoney(Math.max(0, preTaxTaxable - discountOnTaxable));
  const nontaxableSubtotal = roundMoney(Math.max(0, roundedNontaxableFees - discountOnNontaxable));
  const taxAmount = isTaxExempt ? 0 : roundMoney(taxableSubtotal * taxRate);

  const adjustments = [];
  if (roundedDiscount > 0) {
    adjustments.push({
      type: PAYMENT_ADJUSTMENT_TYPES.TAXABLE_DISCOUNT,
      label: discountLabel,
      amount: roundedDiscount
    });
  }

  const nontaxableAdjustments = adjustments
    .filter((entry) => entry.type === PAYMENT_ADJUSTMENT_TYPES.NONTAXABLE_ADJUSTMENT)
    .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  const deposits = adjustments
    .filter((entry) => entry.type === PAYMENT_ADJUSTMENT_TYPES.DEPOSIT)
    .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);

  const grossTotal = roundMoney(
    taxableSubtotal + nontaxableSubtotal + taxAmount + nontaxableAdjustments
  );
  const amountDue = roundMoney(grossTotal - deposits);

  return {
    amountDue,
    discountAmount: roundedDiscount,
    breakdown: {
      taxableSubtotal,
      nontaxableSubtotal,
      taxAmount,
      taxRate,
      adjustments
    }
  };
}
